import React from 'react';

/**
 * The first candidate URL that actually loads, or null while probing and when none does.
 *
 * Avatar URLs built from an email exist for everyone (getAvatarUrl), so "is there a URL" no
 * longer means "is there a picture". A caller with a second source (a sender's own image) needs
 * to know the first one failed before trying the next. The browser caches what the probe loads,
 * so rendering the winner does not fetch it again.
 */
export function useFirstLoadingImage(
  candidates: readonly (string | null | undefined)[]
): string | null {
  const urls = candidates.filter((url): url is string => Boolean(url));
  const key = urls.join('\n');
  const [found, setFound] = React.useState<{
    readonly key: string;
    readonly url: string | null;
  }>({ key: '', url: null });

  React.useEffect(() => {
    let cancelled = false;
    const tryAt = (index: number) => {
      if (cancelled) {
        return;
      }
      if (index >= urls.length) {
        setFound({ key, url: null });
        return;
      }
      const probe = new Image();
      probe.onload = () => {
        if (!cancelled) {
          setFound({ key, url: urls[index] });
        }
      };
      probe.onerror = () => tryAt(index + 1);
      probe.src = urls[index];
    };
    tryAt(0);
    return () => {
      cancelled = true;
    };
    // `key` stands for `urls`: same joined list, same candidates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return found.key === key ? found.url : null;
}
